/**
 * In-memory mock for `gtwinClient.scene.items.*`,
 * `gtwinClient.scene.presets.{upsert,remove}`, and
 * `gtwinClient.package.exportGtwin()` (WP-1).
 *
 * Implements the `SceneItemsAdapter` / `ScenePresetsAdapter` /
 * `PackageAdapter` contracts that the real platform SDK will expose
 * once `SCENE_ITEMS_SDK_SPEC.md` lands. Until then, this is the **live**
 * data path for the gTwin-first viewer workflow when
 * `VITE_GTWIN_SCENE_ITEMS=mock` (the default).
 *
 * Every mutation:
 *   - validates the canonical `SceneItem` shape,
 *   - mutates a per-gtwin in-memory store,
 *   - emits an audit row via `recordAudit`.
 *
 * Test-friendly: `__resetSceneItemsMock()` clears all state.
 */

import type { SceneItem } from "./types.js";
import {
  InvalidSlotError,
  NotAuthorizedError,
  NotFoundError,
} from "./errors.js";
import {
  throwIfInvalid,
  type PackageAdapter,
  type SceneItemInput,
  type SceneItemsAdapter,
  type ScenePresetInput,
  type ScenePresetsAdapter,
} from "./scene-items.js";
import { recordAudit } from "./audit.js";

interface MockState {
  items: Map<string, Map<string, SceneItem>>;
  presets: Map<string, Map<string, ScenePresetInput>>;
  activePreset: Map<string, string>;
  layerOrder: Map<string, string[]>;
  versions: Map<string, number>;
  permissionDenied: Set<string>;
  forceFailure: Set<string>;
}

const state: MockState = {
  items: new Map(),
  presets: new Map(),
  activePreset: new Map(),
  layerOrder: new Map(),
  versions: new Map(),
  permissionDenied: new Set(),
  forceFailure: new Set(),
};

function bumpVersion(gtwinId: string): number {
  const next = (state.versions.get(gtwinId) ?? 0) + 1;
  state.versions.set(gtwinId, next);
  return next;
}

function ensureItems(gtwinId: string): Map<string, SceneItem> {
  if (!state.items.has(gtwinId)) state.items.set(gtwinId, new Map());
  return state.items.get(gtwinId) as Map<string, SceneItem>;
}

function ensurePresets(gtwinId: string): Map<string, ScenePresetInput> {
  if (!state.presets.has(gtwinId)) state.presets.set(gtwinId, new Map());
  return state.presets.get(gtwinId) as Map<string, ScenePresetInput>;
}

function ensureLayerOrder(gtwinId: string): string[] {
  if (!state.layerOrder.has(gtwinId)) state.layerOrder.set(gtwinId, []);
  return state.layerOrder.get(gtwinId) as string[];
}

function checkAccess(gtwinId: string): void {
  if (state.permissionDenied.has(gtwinId)) {
    throw new NotAuthorizedError(`mock: tenant lacks scene-write on ${gtwinId}`);
  }
  if (state.forceFailure.has(gtwinId)) {
    throw new Error(`mock: forced failure for ${gtwinId}`);
  }
}

function toSceneItem(input: SceneItemInput): SceneItem {
  const endpoints: Record<string, string> = {};
  for (const [key, value] of Object.entries(input.endpoints)) {
    if (value && typeof value.href === "string") endpoints[key] = value.href;
  }
  return {
    id: input.id,
    layerKind: input.layerKind,
    crs: input.sourceCrs,
    bbox: input.bbox,
    endpoints,
    visible: true,
    style: {
      title: input.title,
      artifactKind: input.artifactKind,
      slot: input.slot,
      provenance: input.provenance,
      readiness: input.readiness ?? ["visualization-ready"],
      intelligenceLevel: input.intelligenceLevel ?? "L1",
      ...(input.extensions ?? {}),
    },
  };
}

const itemsAdapter: SceneItemsAdapter = {
  async add(gtwinId, input) {
    checkAccess(gtwinId);
    throwIfInvalid(input);
    const items = ensureItems(gtwinId);
    if (items.has(input.id)) {
      throw new InvalidSlotError(
        `mock: scene item "${input.id}" already exists in ${gtwinId}`,
      );
    }
    const item = toSceneItem(input);
    items.set(item.id, item);
    ensureLayerOrder(gtwinId).push(item.id);
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.items.add",
      target: item.id,
      newVersion,
      details: {
        slot: input.slot,
        layerKind: input.layerKind,
        artifactKind: input.artifactKind,
      },
    });
    return item;
  },

  async remove(gtwinId, itemId) {
    checkAccess(gtwinId);
    const items = ensureItems(gtwinId);
    if (!items.has(itemId)) {
      throw new NotFoundError(`mock: scene item "${itemId}" not found in ${gtwinId}`);
    }
    items.delete(itemId);
    const order = ensureLayerOrder(gtwinId);
    const idx = order.indexOf(itemId);
    if (idx >= 0) order.splice(idx, 1);
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.items.remove",
      target: itemId,
      newVersion,
    });
  },

  async patch(gtwinId, patches) {
    checkAccess(gtwinId);
    const items = ensureItems(gtwinId);
    const updated: SceneItem[] = [];
    for (const patch of patches) {
      const existing = items.get(patch.id);
      if (!existing) {
        throw new NotFoundError(`mock: scene item "${patch.id}" not found in ${gtwinId}`);
      }
      const next: SceneItem = {
        ...existing,
        visible: patch.visible ?? existing.visible,
        style: {
          ...(existing.style ?? {}),
          ...(patch.style ?? {}),
          ...(typeof patch.opacity === "number" ? { opacity: patch.opacity } : {}),
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.readiness !== undefined ? { readiness: patch.readiness } : {}),
          ...(patch.intelligenceLevel !== undefined
            ? { intelligenceLevel: patch.intelligenceLevel }
            : {}),
        },
      };
      items.set(patch.id, next);
      updated.push(next);
    }
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.items.patch",
      target: patches.map((patch) => patch.id).join(","),
      newVersion,
      details: { count: patches.length },
    });
    return updated;
  },

  async reorder(gtwinId, layerOrder) {
    checkAccess(gtwinId);
    const items = ensureItems(gtwinId);
    for (const id of layerOrder) {
      if (!items.has(id)) {
        throw new InvalidSlotError(`mock: unknown layer "${id}" in reorder`);
      }
    }
    state.layerOrder.set(gtwinId, [...layerOrder]);
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.items.reorder",
      target: layerOrder.join(","),
      newVersion,
    });
  },

  async copyFrom(targetGtwinId, input) {
    checkAccess(targetGtwinId);
    checkAccess(input.sourceGtwinId);
    const sourceItems = ensureItems(input.sourceGtwinId);
    const targetItems = ensureItems(targetGtwinId);
    const ids =
      input.itemIds && input.itemIds.length > 0
        ? [...input.itemIds]
        : Array.from(sourceItems.keys());
    const copied: string[] = [];
    for (const id of ids) {
      const item = sourceItems.get(id);
      if (!item) {
        throw new InvalidSlotError(
          `mock: scene item "${id}" not found in source ${input.sourceGtwinId}`,
        );
      }
      if (targetItems.has(id)) {
        throw new InvalidSlotError(
          `mock: scene item "${id}" already exists in target ${targetGtwinId}`,
        );
      }
      const clone: SceneItem = {
        ...item,
        style: item.style ? { ...item.style } : undefined,
      };
      targetItems.set(id, clone);
      copied.push(id);
      if (input.deleteFromSource) {
        sourceItems.delete(id);
      }
    }
    const targetOrder = ensureLayerOrder(targetGtwinId);
    for (const id of copied) {
      if (!targetOrder.includes(id)) targetOrder.push(id);
    }
    if (input.deleteFromSource) {
      const sourceOrder = ensureLayerOrder(input.sourceGtwinId);
      state.layerOrder.set(
        input.sourceGtwinId,
        sourceOrder.filter((id) => !copied.includes(id)),
      );
      bumpVersion(input.sourceGtwinId);
    }
    const targetVersion = bumpVersion(targetGtwinId);
    recordAudit({
      gtwinId: targetGtwinId,
      action: "scene.items.copy-from",
      target: input.sourceGtwinId,
      newVersion: targetVersion,
      details: { copiedItemIds: copied, deleteFromSource: Boolean(input.deleteFromSource) },
    });
    return {
      copied: copied.length,
      items: copied,
      targetVersion,
      deleteFromSource: Boolean(input.deleteFromSource),
    };
  },
};

const presetsAdapter: ScenePresetsAdapter = {
  async activate(gtwinId, presetId) {
    checkAccess(gtwinId);
    const presets = ensurePresets(gtwinId);
    if (!presets.has(presetId)) {
      throw new NotFoundError(`mock: preset "${presetId}" not found in ${gtwinId}`);
    }
    state.activePreset.set(gtwinId, presetId);
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.presets.activate",
      target: presetId,
      newVersion,
    });
  },

  async upsert(gtwinId, preset) {
    checkAccess(gtwinId);
    if (!preset.id || preset.id.trim().length === 0) {
      throw new InvalidSlotError("mock: preset id is required");
    }
    if (!preset.label || preset.label.trim().length === 0) {
      throw new InvalidSlotError("mock: preset label is required");
    }
    const presets = ensurePresets(gtwinId);
    const existed = presets.has(preset.id);
    presets.set(preset.id, preset);
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.presets.upsert",
      target: preset.id,
      newVersion,
      details: {
        existed,
        layerCount: Object.keys(preset.layerVisibility ?? {}).length,
      },
    });
    return preset;
  },

  async remove(gtwinId, presetId) {
    checkAccess(gtwinId);
    const presets = ensurePresets(gtwinId);
    if (!presets.has(presetId)) {
      throw new NotFoundError(`mock: preset "${presetId}" not found in ${gtwinId}`);
    }
    presets.delete(presetId);
    const newVersion = bumpVersion(gtwinId);
    recordAudit({
      gtwinId,
      action: "scene.presets.remove",
      target: presetId,
      newVersion,
    });
  },
};

const packageAdapter: PackageAdapter = {
  async exportGtwin(gtwinId, options) {
    checkAccess(gtwinId);
    const items = Array.from(ensureItems(gtwinId).values());
    const presets = Array.from(ensurePresets(gtwinId).values());
    const payload = {
      schema: "geosoft.gtwin-thin/1",
      gtwinId,
      mode: options?.mode ?? "thin",
      generatedAt: new Date().toISOString(),
      items,
      presets,
      activePreset: state.activePreset.get(gtwinId) ?? null,
      layerOrder: ensureLayerOrder(gtwinId),
    };
    const text = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(text).byteLength;
    options?.onProgress?.(bytes, bytes);
    const blob =
      typeof Blob !== "undefined"
        ? new Blob([text], { type: "application/json" })
        : undefined;
    const sha256 = await sha256Hex(text);
    recordAudit({
      gtwinId,
      action: "package.export",
      target: gtwinId,
      details: { mode: options?.mode ?? "thin", bytes, sha256 },
    });
    return {
      url: `mock://gtwin/${encodeURIComponent(gtwinId)}.gtwin`,
      bytes,
      sha256,
      checksumVerified: true,
      blob,
    };
  },
};

async function sha256Hex(input: string): Promise<string | undefined> {
  try {
    if (typeof crypto !== "undefined" && crypto.subtle) {
      const data = new TextEncoder().encode(input);
      const buf = await crypto.subtle.digest("SHA-256", data);
      return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch {
    /* fall through */
  }
  return undefined;
}

export interface SceneItemsMockSurface {
  items: SceneItemsAdapter;
  presets: ScenePresetsAdapter;
  package: PackageAdapter;
  __debugItems(gtwinId: string): readonly SceneItem[];
  __debugPresets(gtwinId: string): readonly ScenePresetInput[];
  __setPermissionDenied(gtwinId: string, denied: boolean): void;
  __setForceFailure(gtwinId: string, fail: boolean): void;
  __reset(): void;
}

export const sceneItemsMock: SceneItemsMockSurface = {
  items: itemsAdapter,
  presets: presetsAdapter,
  package: packageAdapter,
  __debugItems: (gtwinId) => Array.from(state.items.get(gtwinId)?.values() ?? []),
  __debugPresets: (gtwinId) => Array.from(state.presets.get(gtwinId)?.values() ?? []),
  __setPermissionDenied: (gtwinId, denied) => {
    if (denied) state.permissionDenied.add(gtwinId);
    else state.permissionDenied.delete(gtwinId);
  },
  __setForceFailure: (gtwinId, fail) => {
    if (fail) state.forceFailure.add(gtwinId);
    else state.forceFailure.delete(gtwinId);
  },
  __reset: () => {
    state.items.clear();
    state.presets.clear();
    state.activePreset.clear();
    state.layerOrder.clear();
    state.versions.clear();
    state.permissionDenied.clear();
    state.forceFailure.clear();
  },
};

export function __resetSceneItemsMock(): void {
  sceneItemsMock.__reset();
}
